import { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, ShieldCheck, Mail, Eye, EyeOff, KeyRound, Smartphone, Pencil, Phone, MapPin, Briefcase } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import ProfilePhotoInput from "@/components/site/ProfilePhotoInput";

export const DESIGNATIONS = [
  "Teacher", "Nursery Teacher", "Receptionist", "Helper",
  "Coordinator", "Caretaker", "Administrator", "Custom",
];

// System roles that map to actual permission scopes.
// "staff" = coordinator (inquiries + admissions + own me tab)
// All others = self-service only (own attendance + own payslips + own profile)
export const ROLE_OPTIONS = [
  { value: "staff", label: "Coordinator (inquiries + admissions)" },
  { value: "teacher", label: "Teacher" },
  { value: "caretaker", label: "Caretaker" },
  { value: "security", label: "Security" },
  { value: "van_driver", label: "Van Driver" },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label.split(" (")[0]]));
const roleBadgeStyle = (role) => ({
  staff: "bg-sky-100 text-ark-skyDeep",
  teacher: "bg-emerald-100 text-emerald-700",
  caretaker: "bg-pink-100 text-pink-600",
  security: "bg-amber-100 text-amber-700",
  van_driver: "bg-violet-100 text-violet-700",
}[role] || "bg-slate-100 text-slate-600");

// Reusable designation picker (list + free-text when "Custom")
export const DesignationField = ({ value, onChange, testId = "designation" }) => {
  const isPreset = DESIGNATIONS.includes(value) && value !== "Custom";
  const [custom, setCustom] = useState(!isPreset && !!value);
  return (
    <div className="space-y-2">
      <Select
        value={custom ? "Custom" : value || ""}
        onValueChange={(v) => {
          if (v === "Custom") { setCustom(true); onChange(""); }
          else { setCustom(false); onChange(v); }
        }}
      >
        <SelectTrigger data-testid={`${testId}-select`} className="rounded-2xl h-11 border-slate-200">
          <SelectValue placeholder="Select role / designation" />
        </SelectTrigger>
        <SelectContent>
          {DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
        </SelectContent>
      </Select>
      {custom && (
        <Input
          data-testid={`${testId}-custom`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type custom role"
          className="rounded-2xl h-11 border-slate-200"
        />
      )}
    </div>
  );
};

const EditStaffDialog = ({ staff, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(staff);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/staff/${staff.id}`, {
        name: form.name, role: form.role || "staff", designation: form.designation || "",
        phone: form.phone || "", address: form.address || "", photo: form.photo || "",
        monthly_salary: parseFloat(form.monthly_salary) || 0,
      });
      toast.success("Profile updated.");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(staff); }}>
      <DialogTrigger asChild>
        <button data-testid={`staff-edit-${staff.id}`}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-teal-50 text-ark-tealDeep" title="Edit profile">
          <Pencil className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-4xl max-h-[90vh] overflow-y-auto" data-testid={`edit-dialog-${staff.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Edit staff profile</DialogTitle>
          <DialogDescription>Update {staff.email}'s details and role.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <ProfilePhotoInput value={form.photo} onChange={(v) => set("photo", v)} fallback={(form.name || "?").charAt(0).toUpperCase()} testId={`edit-photo-${staff.id}`} />
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Name</Label>
            <Input data-testid={`edit-name-${staff.id}`} value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-2xl h-11 border-slate-200" />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">System Role</Label>
            <Select value={form.role || "staff"} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid={`edit-role-${staff.id}`} className="rounded-2xl h-11 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`edit-role-opt-${r.value}-${staff.id}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Designation (display)</Label>
            <DesignationField value={form.designation || ""} onChange={(v) => set("designation", v)} testId={`edit-designation-${staff.id}`} />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Phone</Label>
            <Input data-testid={`edit-phone-${staff.id}`} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" className="rounded-2xl h-11 border-slate-200" />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Address</Label>
            <Textarea data-testid={`edit-address-${staff.id}`} value={form.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} className="rounded-2xl border-slate-200 resize-none" />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Monthly Salary (₹)</Label>
            <Input type="number" data-testid={`edit-salary-${staff.id}`} value={form.monthly_salary ?? ""} onChange={(e) => set("monthly_salary", e.target.value)} placeholder="e.g. 25000" className="rounded-2xl h-11 border-slate-200" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid={`edit-save-${staff.id}`}
              className="rounded-full bg-ark-teal hover:bg-ark-tealDeep text-white font-display font-semibold w-full sm:w-auto">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ staff, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/staff/${staff.id}/password`, { password: pwd });
      toast.success(`Password reset for ${staff.name || staff.email}.`);
      setPwd("");
      setOpen(false);
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not reset password.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`staff-reset-${staff.id}`}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-sky-50 text-ark-skyDeep"
          title="Reset password"
        >
          <KeyRound className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-4xl" data-testid={`reset-dialog-${staff.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for <span className="font-semibold text-ark-ink">{staff.name || staff.email}</span> and share it with them securely.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">New Password</Label>
            <div className="relative">
              <Input
                data-testid={`reset-password-input-${staff.id}`}
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Min 6 characters"
                className="rounded-2xl h-11 border-slate-200 pr-11"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              data-testid={`reset-submit-${staff.id}`}
              className="rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold w-full sm:w-auto"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Set New Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StaffManager = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", designation: "", phone: "", address: "", photo: null, monthly_salary: "" });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(data);
    } catch {
      toast.error("Failed to load staff.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const createStaff = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/staff", { ...form, monthly_salary: parseFloat(form.monthly_salary) || 0 });
      toast.success("Staff account created.");
      setForm({ name: "", email: "", password: "", role: "staff", designation: "", phone: "", address: "", photo: null, monthly_salary: "" });
      fetchStaff();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create staff.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const removeStaff = async (id) => {
    try {
      await api.delete(`/admin/staff/${id}`);
      setStaff((arr) => arr.filter((s) => s.id !== id));
      toast.success("Staff removed.");
    } catch {
      toast.error("Could not remove staff.");
    }
  };

  const reset2fa = async (id, label) => {
    try {
      await api.post(`/admin/staff/${id}/reset-2fa`);
      toast.success(`2FA reset for ${label}. They'll set it up again at next login.`);
    } catch {
      toast.error("Could not reset 2FA.");
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="border-0 rounded-3xl shadow-ark-soft lg:col-span-1 h-fit" data-testid="staff-create-card">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-ark-ink flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-ark-teal" /> Add Staff
          </h3>
          <p className="text-sm text-slate-500 mt-1">Give reception staff walk-in access.</p>
          <form onSubmit={createStaff} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Name</Label>
              <Input
                data-testid="staff-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Staff name"
                className="rounded-2xl h-11 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Email</Label>
              <Input
                data-testid="staff-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="staff@littleark.in"
                className="rounded-2xl h-11 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  data-testid="staff-password-input"
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Min 6 characters"
                  className="rounded-2xl h-11 border-slate-200 pr-11"
                />
                <button
                  type="button"
                  data-testid="staff-password-toggle"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">System Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="staff-role-select" className="rounded-2xl h-11 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`staff-role-opt-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Coordinator can add walk-in inquiries and share admission links. Others only mark attendance and view payslips.</p>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Designation (display)</Label>
              <DesignationField value={form.designation} onChange={(v) => setForm((f) => ({ ...f, designation: v }))} testId="staff-designation" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Phone</Label>
              <Input data-testid="staff-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+91 …" className="rounded-2xl h-11 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Address</Label>
              <Textarea data-testid="staff-address-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} rows={2} placeholder="Home address" className="rounded-2xl border-slate-200 resize-none" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Monthly Salary (₹)</Label>
              <Input type="number" data-testid="staff-salary-input" value={form.monthly_salary} onChange={(e) => setForm((f) => ({ ...f, monthly_salary: e.target.value }))} placeholder="e.g. 25000" className="rounded-2xl h-11 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Profile Photo</Label>
              <ProfilePhotoInput value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} fallback={(form.name || "?").charAt(0).toUpperCase()} testId="staff-photo" />
            </div>
            <Button
              type="submit"
              disabled={saving}
              data-testid="staff-create-btn"
              className="w-full rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create Staff Account"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-0 rounded-3xl shadow-ark-soft lg:col-span-2" data-testid="staff-list-card">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-ark-ink">Staff Accounts</h3>
          <div className="mt-4 space-y-3">
            {loading ? (
              <div className="text-slate-500 py-8 text-center">Loading…</div>
            ) : staff.length === 0 ? (
              <div className="text-slate-500 py-8 text-center" data-testid="staff-empty">
                No staff accounts yet. Add one to give walk-in access.
              </div>
            ) : (
              staff.map((s) => (
                <div
                  key={s.id}
                  data-testid={`staff-row-${s.id}`}
                  className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-full overflow-hidden bg-gradient-to-br from-ark-sky to-ark-teal flex items-center justify-center font-display font-bold text-white shrink-0">
                      {s.photo ? (
                        <img src={s.photo} alt={s.name || "Staff"} className="h-full w-full object-cover" data-testid={`staff-photo-${s.id}`} />
                      ) : (
                        (s.name || s.email).charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-ark-ink truncate flex items-center gap-2">
                        {s.name || "Staff"}
                        {s.designation && (
                          <span className="text-[11px] font-semibold text-ark-tealDeep bg-teal-50 rounded-full px-2 py-0.5" data-testid={`staff-designation-${s.id}`}>
                            {s.designation}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 truncate">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</span>
                        {s.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge data-testid={`staff-role-badge-${s.id}`} className={`border-0 rounded-full font-semibold hidden sm:inline-flex ${roleBadgeStyle(s.role)}`}>
                      <ShieldCheck className="h-3 w-3 mr-1" /> {ROLE_LABEL[s.role] || "Staff"}
                    </Badge>
                    <EditStaffDialog staff={s} onDone={fetchStaff} />
                    <ResetPasswordDialog staff={s} onDone={fetchStaff} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`staff-reset2fa-${s.id}`}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-amber-50 text-amber-600"
                          title="Reset authenticator (2FA)"
                        >
                          <Smartphone className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Reset 2FA for {s.name || s.email}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Their authenticator will be removed and they'll be asked to scan a new QR code
                            at their next login. Use this if they lost their phone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => reset2fa(s.id, s.name || s.email)} className="bg-amber-500 hover:bg-amber-600">
                            Reset 2FA
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`staff-delete-${s.id}`}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-red-500"
                          title="Remove staff"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {s.name || s.email}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            They will immediately lose access to the staff panel. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeStaff(s.id)} className="bg-red-500 hover:bg-red-600">
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffManager;
